import { useEffect, useMemo, useState } from 'react'
import { bumpMyGamesPlayed } from '../../../lib/api/profile'
import { PageHeader } from '../../../components/PageHeader'
import { RankMedal } from '../../../components/RankMedal'
import { COLORS_BY_ID, type ColorId } from '../data/colors'
import { ColorCardImage } from '../components/ColorCardImage'
import { AllRoundsRecap, type RecapRound } from '../components/AllRoundsRecap'
import { CATEGORIES } from '../data/categories'
import { scoreMatch } from '../engine/scoring'
import type { ColorfulState } from '../engine/types'
import { BackgroundTitle } from '../../../components/BackgroundTitle'
import { useT } from '../../../i18n'

interface Props {
  state: ColorfulState
  onPlayAgain: () => void
  onExit: () => void
}

/**
 * Final reveal + scoreboard. Per the rules, every round's picks stay
 * face-down until the end of the match, then the table walks category
 * by category recalling the subject and comparing colors. We mirror
 * that as a stepped reveal: one round at a time, then the final
 * leaderboard + verdict.
 */
export function ColorfulResultsScreen({ state, onPlayAgain, onExit }: Props) {
  const t = useT()
  // Every LOCAL Colorful game (solo vs AI and hot-seat) ends on this
  // screen exactly once; online rooms never route here, so this is
  // the one safe place to count the game.
  useEffect(() => { bumpMyGamesPlayed() }, [])

  const result = useMemo(() => scoreMatch(state.rounds, state.players), [state])
  const { perRound, points, coopWin, winners } = result

  const sortedPlayers = [...state.players]
    .sort((a, b) => (points[b.id] ?? 0) - (points[a.id] ?? 0))

  const totalRounds = state.rounds.length
  const [step, setStep] = useState(0)
  const isFinal = step >= totalRounds

  // ── Per-round reveal step ──────────────────────────────────────────────
  if (!isFinal) {
    const round = state.rounds[step]
    const rs = perRound[step]
    const theme = CATEGORIES.find(c => c.id === round.categoryId)?.theme ?? '…'
    const winningColor = rs.majorityColors.length === 1 ? rs.majorityColors[0] : null
    const sortedGroups = Object.entries(rs.groups).sort((a, b) => b[1].length - a[1].length)
    const isLastRound = step + 1 >= totalRounds

    return (
      <div className="screen">
        <BackgroundTitle text="Colorful" color="#74705F" />
        <PageHeader
          title={t('Reveal · Round {n} of {total}', { n: step + 1, total: totalRounds })}
          subtitle={theme}
          onBack={onExit}
        />
        <div className="px-5 py-5 space-y-5 overflow-y-auto pb-32 glass-panes">
          <div className="card-lg p-5 text-center bg-linear-to-br
                          from-game-colorful-light to-white border-2 border-game-colorful/40">
            <p className="text-xs font-semibold text-brand-muted uppercase tracking-widest mb-1">
              {t('The subject was')}
            </p>
            <p className="font-display text-3xl font-bold text-brand-text leading-tight">
              {round.subject}
            </p>
            {rs.unanimous && (
              <p className="text-xs text-iet-green font-semibold mt-2">{t('✨ Unanimous!')}</p>
            )}
          </div>

          <div className="space-y-2">
            {sortedGroups.map(([color, playerIds]) => {
              const names = playerIds
                .map(id => state.players.find(p => p.id === id)?.name)
                .filter(Boolean) as string[]
              const isMajority = color === winningColor
              return (
                <div key={color}
                     className={`card p-4 flex items-center gap-3
                                 ${isMajority ? 'border-2 border-brand-gold/40 bg-brand-gold/5' : ''}`}>
                  <ColorCardImage color={color as ColorId} size="sm" className="shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-brand-text">
                      {COLORS_BY_ID[color as ColorId].name}
                    </p>
                    <p className="text-xs text-brand-muted truncate">{names.join(', ')}</p>
                  </div>
                  <span className="font-display font-bold text-brand-text text-lg shrink-0">
                    ×{playerIds.length}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
        <div className="floating-actions">
          <button onClick={() => setStep(step + 1)} className="btn-primary">
            {isLastRound ? t('See final scores →') : t('Round {n} →', { n: step + 2 })}
          </button>
        </div>
      </div>
    )
  }

  // ── Final scoreboard ──────────────────────────────────────────────────
  // Winner up top, then the full story: every round's subject and who
  // picked what color — same recap the online rooms show.
  const recapRounds: RecapRound[] = state.rounds
    .map((round, i) => {
      const rs = perRound[i]
      return {
        index: i,
        theme: CATEGORIES.find(c => c.id === round.categoryId)?.theme ?? '…',
        subject: round.subject,
        unanimous: rs?.unanimous ?? false,
        groups: Object.entries(rs?.groups ?? {})
          .sort((a, b) => b[1].length - a[1].length)
          .map(([color, ids]) => ({
            color: color as ColorId,
            names: ids.map(id => state.players.find(p => p.id === id)?.name).filter(Boolean) as string[],
            isMajority: rs.majorityColors.length === 1 && color === rs.majorityColors[0],
          })),
      }
    })
    .filter(r => r.groups.length > 0)

  return (
    <div className="screen">
      <BackgroundTitle text="Colorful" color="#74705F" />
      <PageHeader title={t('Colorful')} subtitle={t('Final results')} onBack={onExit} />

      <div className="px-5 py-5 space-y-5 overflow-y-auto pb-28 glass-panes">
        {coopWin ? (
          <div className="relative card-lg p-6 text-center bg-linear-to-br
                          from-game-colorful-light to-white
                          border-2 border-game-colorful/40 animate-scale-in champion-glow">
            <div className="relative text-6xl mb-2 animate-bounce-soft drop-shadow-xs">🌈</div>
            <p className="relative text-xs font-semibold text-game-colorful uppercase tracking-widest mb-2">
              {t('Co-op Victory')}
            </p>
            <h2 className="relative font-display text-2xl font-bold text-brand-text">
              {t('You all saw the same colors.')}
            </h2>
            <p className="relative text-sm text-brand-muted mt-1">
              {t('Every round was unanimous, a rare and pure form of Colorful.')}
            </p>
          </div>
        ) : winners.length === 0 ? (
          <div className="card-lg p-6 text-center bg-brand-elevated animate-scale-in">
            <div className="text-5xl mb-2">🎭</div>
            <h2 className="font-display text-2xl font-bold text-brand-text">
              {t('No winners this round')}
            </h2>
            <p className="text-sm text-brand-muted mt-1">
              {t('Every category ended in a tie. Your group is beautifully un-aligned.')}
            </p>
          </div>
        ) : (
          <div className="relative card-lg p-6 text-center bg-linear-to-br
                          from-brand-gold/20 to-iet-orange/10
                          border-2 border-brand-gold/40 animate-scale-in champion-glow">
            <div className="relative text-6xl mb-2 animate-bounce-soft drop-shadow-xs">🏆</div>
            <p className="relative text-xs font-semibold text-brand-gold uppercase tracking-widest mb-2">
              {winners.length > 1 ? t('Shared victory') : t('Winner')}
            </p>
            <h2 className="relative font-display text-2xl font-bold text-brand-text leading-tight">
              {winners.map(id => state.players.find(p => p.id === id)?.name).filter(Boolean).join(' & ')}
            </h2>
            <p className="relative text-brand-primary font-bold text-lg mt-2">
              {points[winners[0]] === 1
                ? t('1 point')
                : t('{n} points', { n: points[winners[0]] })}
            </p>
          </div>
        )}

        <div>
          <h3 className="font-semibold text-brand-text mb-3">{t('Leaderboard')}</h3>
          <div className="space-y-2">
            {sortedPlayers.map((p, i) => (
              <div key={p.id} className="card p-4 flex items-center gap-3">
                <RankMedal rank={i + 1} size="md" className="shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-brand-text truncate">{p.name}</p>
                </div>
                <div className="flex flex-col items-end shrink-0">
                  <span className="font-display font-bold text-brand-text">
                    {points[p.id] ?? 0}
                  </span>
                  <span className="text-[10px] text-brand-muted">{t('points')}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <AllRoundsRecap rounds={recapRounds} />
      </div>

      <div className="floating-actions space-y-3">
        <button onClick={onPlayAgain} className="btn-primary">{t('Play again')}</button>
        <button onClick={onExit} className="btn-ghost w-full text-center">{t('Back to games')}</button>
      </div>
    </div>
  )
}
